import { cn } from 'cn';
import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/** Five across on desktop, two across on the phone with the hot tile first. */
export function TileRow({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div role="group" aria-label={label} data-slot="tile-row" className={cn('grid grid-cols-2 gap-3 md:grid-cols-5', className)}>
      {children}
    </div>
  );
}

export type TileTone = 'neutral' | 'hot' | 'warning';

export type TileProps = {
  value: number | null | undefined;
  label: string;
  /** hot: the value in error red (a breach); warning: amber (an early warning, DESIGN.md extension). */
  tone?: TileTone;
  pressed: boolean;
  onPress: () => void;
  description: string;
  loading?: boolean;
  /** Counts unavailable: "n/a", disabled. */
  unavailable?: boolean;
  className?: string;
};

export function Tile({ value, label, tone = 'neutral', pressed, onPress, description, loading, unavailable, className }: TileProps) {
  const hotTone = value && value > 0 ? tone : 'neutral';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-pressed={pressed}
          aria-describedby={undefined}
          disabled={Boolean(unavailable || loading)}
          onClick={onPress}
          data-slot="tile"
          data-tone={hotTone}
          className={cn(
            // 64 px: value (26 px at 1.25) plus label (12 px at 1.25) plus 8 px padding each side; DESIGN.md's 67 was the wireframe's estimate.
            'flex min-h-16 flex-col items-start justify-between rounded-xl border border-beam-line bg-beam-paper px-4 py-2 text-left transition-colors duration-[140ms] ease-beam hover:bg-beam-cream-deep disabled:cursor-default disabled:hover:bg-beam-paper',
            pressed && 'bg-beam-cream-deep shadow-[inset_0_-2px_0_0_var(--beam-signature)]',
            className,
          )}
        >
          {loading ? (
            <Skeleton className="h-7 w-10" aria-hidden="true" />
          ) : (
            <span className={cn('font-heading tabular text-[26px] leading-[1.25] font-medium', hotTone === 'hot' && 'text-beam-error', hotTone === 'warning' && 'text-beam-warning')}>
              {unavailable ? 'n/a' : value ?? 0}
            </span>
          )}
          <span className="text-xs leading-tight text-beam-text-muted">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{unavailable ? 'Counts unavailable' : description}</TooltipContent>
    </Tooltip>
  );
}
